import React from "react";
import { Fragment, useState } from "react";

import { ToastContainer } from "react-toastify";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";

import { lookupOrder, lookupOrderByEmail } from "./orderAccess";

import {
  TruckIcon,
  CameraIcon,
  ArrowsExpandIcon,
  ChatIcon,
  PhoneIcon,
  MailIcon,
  NewspaperIcon,
} from "@heroicons/react/outline";

import { useNavigate } from "react-router-dom";

export default function Login() {
  const [orderNumber, setOrderNumber] = useState(
    localStorage.getItem("eziceportal_orderNumber")
  );
  const [lastName, setLastName] = useState(
    localStorage.getItem("eziceportal_lastName")
  );
  const [trackingNumber, setTrackingNumber] = useState(null);
  const [order, setOrder] = useState(null);
  const [province, setProvince] = useState("");
  const [orderAnnouncements, setOrderAnnouncements] = useState([]);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotModeEmail, setForgotModeEmail] = useState("");
  const navigate = useNavigate();

  async function fetchOrder() {
    try {
      await lookupOrder(orderNumber, lastName);

      navigate("../home", { replace: true });
    } catch (e) {
      toast.error(
        e.response?.status === 429
          ? "Too many attempts. Please wait a moment and try again."
          : "Incorrect Last Name or Order Number."
      );
    }
  }

  async function fetchOrderByEmail() {
    try {
      await lookupOrderByEmail(forgotModeEmail);

      navigate("../home", { replace: true });
    } catch (e) {
      toast.error(
        e.response?.status === 429
          ? "Too many attempts. Please wait a moment and try again."
          : "We couldn't find an order with that email. Please try again."
      );
    }
  }

  function clearOrder() {
    setOrder(null);
    setTrackingNumber("");
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") {
      fetchOrder();
    }
  }

  return (
    <div className="grid place-items-center h-screen bg-gray-100 bg-skating bg-cover overflow-scroll">
      <div className="flex text-center bk2:flex-col">
      <div className="flex flex-col items-center max-w-2xl bk4:max-w-xs text-center bg-white/75 pt-8 p-4">
        <div className="items-center font-suigeneris text-4xl">
            Customer Portal
          </div>
    

          <div className="flex flex-col">
            <div className="flex mt-8 bk4:flex-col">
              <div className="flex max-w-xs pl-2 pr-2 bk4:mb-4">
                <div className="-mt-2">
                  <TruckIcon className="h-12 w-12" aria-hidden="true" />
                </div>
                <div className="flex flex-col">
                  <div className="items-center font-semibold mb-4">
                  Track your Order
                  </div>
                  <div className="items-center text-sm">
                  Follow your order's progress from our warehouse to your front door.
                  </div>
                </div>
              </div>

              <div className="flex max-w-xs pl-2 pr-2 bk4:mb-4">
                <div className="-mt-2 ml-3">
                  <NewspaperIcon className="h-12 w-12" aria-hidden="true" />
                </div>
                <div className="flex flex-col">
                  <div className="items-center font-semibold mb-4">
                  View Rink Details
                  </div>
                  <div className="items-center text-sm">
                  Review previous order details, parts lists, and assembly instructions.
                  </div>
                </div>
              </div>
            </div>

            <div className="flex bk4m:mt-16 bk4:flex-col bk4:mt-0">
              <div className="flex max-w-xs pl-2 pr-2 bk4:mb-4">
                <div className="-mt-2">
                  <ArrowsExpandIcon className="h-12 w-12" aria-hidden="true" />
                </div>
                <div className="flex flex-col">
                  <div className="items-center font-semibold mb-4">
                  Upgrade your Rink
                  </div>
                  <div className="items-center text-sm">
                  Easily increase your rink's size, or add taller boards and PRO Netting.
                  </div>
                </div>
              </div>

              <div className="flex max-w-xs pl-2 pr-2">
                <div className="-mt-2">
                  <ChatIcon className="h-12 w-12" aria-hidden="true" />
                </div>
                <div className="flex flex-col ml-6">
                  <div className="items-center font-semibold">Speak with a</div>
                  <div className="items-center font-semibold mb-4">
                    Backyard Rink Expert
                  </div>
                  <div className="flex items-center text-sm justify-center">
                    <PhoneIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                    (262) 394-2360
                  </div>
                  <div className="flex items-center text-sm justify-center">
                    <MailIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                    expert@ezicerinks.com
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center bg-white pt-16 pb-4">
          <h1 className="text-2xl mb-4 font-semibold text-gray-900">
            EZ Login
          </h1>

          {!forgotMode && (
            <Fragment>
              <div className="flex items-center flex-col">
                <div className="flex flex-col">
                  <label className="mb-0">
                    Order Number{" "}
                    <span
                      className="help-tip"
                      data-multiline="true"
                      data-tip="Your Order Number can be found in your order confirmation email."
                    ></span>
                  </label>
                  <input
                    className="w-48 appearance-none inline-block w-full bg-none bg-white border border-gray-300 rounded-md pl-2 pr-2 py-2 text-base text-gray-900 focus:outline-none focus:ring-ezice-blue-500 focus:border-ezice-blue-500 sm:text-sm"
                    type="text"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    onKeyDown={handleKeyDown}
                    data-test="order"
                  ></input>
                </div>
                <div className="flex flex-col">
                  <label className="mb-0">Last Name:</label>
                  <input
                    className="w-48 mb-0 appearance-none inline-block w-full bg-none bg-white border border-gray-300 rounded-md pl-2 pr-2 py-2 text-base text-gray-900 focus:outline-none focus:ring-ezice-blue-500 focus:border-ezice-blue-500 sm:text-sm"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    data-test="name"
                  ></input>
                </div>
                <div className="flex text-red text-xs mt-4 w-full px-4">
                  <p>
                    Don't have your Order Number? Click{" "}
                    <a
                      className="cursor-pointer"
                      onClick={() => setForgotMode(true)}
                    >
                      here
                    </a>
                  </p>
                </div>
                <div className="flex flex-col mt-2">
                  <button
                    className="flex inline-block p-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-ezice-blue-600 hover:bg-ezice-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ezice-blue-500"
                    onClick={fetchOrder}
                    data-test="lookup"
                  >
                    Login
                  </button>
                </div>
              </div>
            </Fragment>
          )}

          {forgotMode && (
            <Fragment>
              <div className="flex items-center flex-col px-14">
                <div className="flex flex-col">
                  <label className="mb-0">
                    Email{" "}
                    <span
                      className="help-tip"
                      data-multiline="true"
                      data-tip="The email you used for your EZ ICE order"
                    ></span>
                  </label>
                  <input
                    className="w-48 appearance-none inline-block w-full bg-none bg-white border border-gray-300 rounded-md pl-2 pr-2 py-2 text-base text-gray-900 focus:outline-none focus:ring-ezice-blue-500 focus:border-ezice-blue-500 sm:text-sm"
                    type="text"
                    value={forgotModeEmail}
                    onChange={(e) => setForgotModeEmail(e.target.value)}
                  ></input>
                </div>

                <div className="flex mt-8">
                  <button
                    className="flex inline-block ml-2 p-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-ezice-blue-600 hover:bg-ezice-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ezice-blue-500"
                    onClick={fetchOrderByEmail}
                  >
                    Lookup
                  </button>
                </div>
              </div>
            </Fragment>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <ReactTooltip place="bottom" />
    </div>
  );
}
